import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import { InformationCircleIcon, AcademicCapIcon, PlayCircleIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import { getResourceBySlug } from '../../../lib/db';

const TeamHealthChecksKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'Medium - Requires facilitation skills and team coordination'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '5 Hours Prep | 1-2 hours per team per quarter'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: '1 team + (scales up to 400 people / 50 teams)'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'People are tired of surveys and you\'re not getting engagement which means you lack insights into how teams are doing and what the opportunities are.'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'When you don\'t have strong signal on what the de-motivators are per team and across all teams.'
    }
  ];

  useEffect(() => {
    getResourceBySlug('team-health-checks').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "Team Health Checks"}
        subtitle={resource?.description || "A comprehensive guide to running effective team health checks"}
        category={resource?.category || "Playbook"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "10 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>
        {/* Quote first */}
        <div className="italic text-gray-300 text-lg p-6 border border-gray-800 rounded-lg bg-gray-900/50 mb-8">
          <p className="mb-2">
            "People aren't responding to surveys and even when they do, I don't feel like I have a good feedback loop between People Team &lt;&gt; Team members"
          </p>
          <p className="text-gray-400 text-sm">-All Startup People Leaders at some point</p>
        </div>
        {/* This Playbook is for you section */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if either of the following are true:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Teams are exhausted by surveys but you still want to understand where the continuous improvement opportunities are in your ways of working</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You can't "see" where the opportunities are for improvement, where teams need help and what kind of help they need and from who</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You are looking for a more human and engaging way to run an all-hands retrospective on company culture</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You want to get a heat-map of improvement themes across all teams that includes insights into how supported folks are from managers, suitable process/tools, clarity and alignment to mission and more</span>
          </li>
        </ul>
        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>
        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed src="https://www.loom.com/embed/880322fcf12641108399d2d05684ce2a?sid=eb8fc20d-cf37-4f39-9d9c-6bc3c4f683a5" title="Watch the Loom video" />
          <div className="text-center text-gray-400 text-sm mt-3">Walkthrough: How to run a Team Health Check</div>
        </div>
        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              Welcome to your step-by-step guide on establishing a team health check aka an incredible feedback loop between you and your people. You'll get to see and hear first hand what's stopping teams do their best work that can provide the data to prioritise people team and people manager initiatives.
            </p>
            <p className="text-lg">
              This approach takes inspiration from Spotify's squad health check for engineering teams but extends it and makes it accessible to all teams in your startup.
            </p>
            <p className="text-lg">
              This guide outlines why, when, and how to start a team health check across all your teams and create a long lasting feedback loop that engages managers, executives and team members in a conversation around culture - aka how we work.
            </p>
            <div className="flex items-center font-semibold mt-4 text-lg">
              Let's get into it <span className="ml-2 text-xl">🤘</span>
            </div>
          </div>
        </div>
        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3 pl-5 list-disc">
            <li className="text-gray-300">A facilitator per team (people manager or someone good at holding space)</li>
            <li className="text-gray-300">Miro or equivalent if you're remote</li>
            <li className="text-gray-300">Magic whiteboard, stickies and sharpies if you're face to face</li>
            <li className="text-gray-300">About 2 days to prep and about 2 hours per team to run and debrief</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TeamHealthChecksKeyInfo; 